// help-menu.ts — the help SUBMENU on the Bridge header (owner IA ruling,
// help-portal program): the circle-help control opens a small menu listing
// the help surfaces — Knowledgebase (/help/), API documentation (/help/api/)
// and Release Notes (/help/#/release-notes) — each in a NEW window, because
// help must never navigate a live cockpit away (#432's rule, kept).
//
// The IA ITSELF is no longer written here. It lives in ONE descriptor,
// pi-stack/src/client/help-entries.ts, which the Helm rail (helm-bridge/
// src/client/rail.ts) consumes as well — the two menus were duplicate
// registers of the same list, and the owner's third entry would otherwise
// have had to be added twice. This module owns the console-side PRIMITIVE
// (popover trigger, dismissal, new-window rule), not the list.
//
// Element-constructed on purpose (no innerHTML): the stub-DOM suite
// (scripts/help-submenu.test.mjs) drives this REAL module — open/close
// wiring, entry hrefs, new-window attributes — the bridge-ui-polish idiom.

import { icon } from './generated/icons.js';
import { mkIconBtn } from './buttons.js';
import { HELP_MENU_ENTRIES, type HelpMenuEntry } from 'pi-stack/client/help-entries.js';

// Re-exported so the console client keeps ONE import site for the IA and the
// existing suite keeps its handle on it — the values come from the shared
// descriptor, never from a second table here.
export { HELP_MENU_ENTRIES };
export type { HelpMenuEntry };

/** Mount the help submenu into `host` (a positioned element in the header).
 * Returns a teardown that removes the document-level dismiss listener. */
export function mountHelpMenu(host: HTMLElement): () => void {
  // The "open help" control rides the icon-button PRIMITIVE (S×T W2, owner
  // screenshot 2026-09-08). It opens a menu, but it is still the help action
  // the owner ruling names — the same call the Helm rail makes (rail.ts) —
  // so aria-haspopup/aria-expanded travel through `attrs` rather than the
  // button being stamped a deferred trigger. Measured before this change:
  // `<button class="hc-iconbtn">` had NO chrome rule left on the console
  // (PR-2 retired the class's paint "to the intent ramp" without moving the
  // site onto it), so it rendered the browser's own 2px outset border and
  // 1px 6px padding — a heavy dark square with the glyph shoved low/right,
  // identical under every style — while buttons-check counted it deferred.
  // mkIconBtn is the DOM lane (stub-DOM drivable: help-submenu.test.mjs).
  const btn = mkIconBtn({
    glyph: 'circle-help',
    label: 'Help',
    id: 'hcHelpBtn',
    attrs: { 'aria-haspopup': 'menu', 'aria-expanded': 'false' },
  });

  const menu = document.createElement('div');
  menu.className = 'hc-menu hc-menu-help';
  menu.id = 'hcHelpMenu';
  menu.setAttribute('role', 'menu');
  menu.hidden = true;

  for (const entry of HELP_MENU_ENTRIES) {
    const a = document.createElement('a');
    a.className = 'hc-menu-item';
    a.setAttribute('role', 'menuitem');
    a.href = entry.href;
    // NEW WINDOW, always: the portal is a separate document; noopener so it
    // cannot reach back into the cockpit.
    a.target = '_blank';
    a.rel = 'noopener';
    a.innerHTML = `${icon(entry.glyph)}<span>${entry.label}</span>`;
    a.addEventListener('click', () => close());
    menu.appendChild(a);
  }

  const close = (): void => {
    menu.hidden = true;
    menu.classList.remove('on');
    btn.setAttribute('aria-expanded', 'false');
  };
  const open = (): void => {
    menu.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    // Class after unhiding so the entrance animation actually runs (the
    // account-menu rule: keyframes played while hidden arrive finished).
    requestAnimationFrame(() => menu.classList.add('on'));
  };

  btn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    if (menu.hidden) open(); else close();
  });

  const onDocClick = (): void => close();
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') close();
  };
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKey);

  host.appendChild(btn);
  host.appendChild(menu);

  return () => {
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onKey);
  };
}
